import React, { useEffect, useRef, useState } from 'react';
import { Pagination as Pag } from 'react-bootstrap';
import _ from 'lodash';
import './Pagination.scss';

const DEFAULT_WINDOW_SIZE = 2;

export interface PaginationProps {
    defaultSelected?: number;
    windowSize?: number;
    size: number;
    href?: (page: number) => string;
    onSelect?: (selected: number) => void;
    selected?: number;
}

const JumpEllipsis = ({ onJump }: { onJump: (p: number) => void }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [val, setVal] = useState('');

    const inputRef = useRef<HTMLInputElement>(null);

    const showInput = isHovered || isFocused;

    useEffect(() => {
        if (showInput && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 10);
        }
    }, [showInput]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const pageNumber = parseInt(val, 10);
            if (!Number.isNaN(pageNumber)) {
                onJump(pageNumber - 1);
            }
            setVal('');
            setIsFocused(false);
            setIsHovered(false);
        }
    };

    return (
        <li
            className='page-item JumpEllipsis-item'
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => setIsFocused(true)}
            style={{
                width: '40px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            {showInput ? (
                <input
                    ref={inputRef}
                    type='number'
                    className='page-link JumpEllipsis-input mb-0'
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => {
                        setIsFocused(false);
                        setVal('');
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder='nr'
                    style={{
                        width: '100%',
                        padding: '1px 1px',
                        textAlign: 'center',
                        boxShadow: 'none',
                        display: 'block',
                        margin: '0',
                        border: '2.6px solid #8f9194b6',
                        color: '#868686'
                    }}
                />
            ) : (
                <span className='page-link' style={{ textAlign: 'center', display: 'block', width: '100%' }}>...</span>
            )}
        </li>
    );
};

const Pagination = ({ defaultSelected = 0, windowSize = DEFAULT_WINDOW_SIZE, size, href = (p) => '#', onSelect = _.identity, selected = undefined }: PaginationProps) => {
    const [page, setPage] = useState(defaultSelected);
    const selectedPage = selected ?? page;

    const clampPage = (p: number) => {
        p = _.clamp(p, 0, size - 1);
        setPage(p);
        onSelect(p);
    };

    const handleClick = (p: number) => (event: React.MouseEvent) => {
        event.preventDefault();
        clampPage(p);
    };

    windowSize = Math.max(0, windowSize);
    let pages;

    if (size <= 7) {
        pages = _.range(0, size);
    } else {
        pages = (selectedPage >= 4 && selectedPage <= size - 5) ? _.range(selectedPage - 1, selectedPage + 2) : (selectedPage > size - 5 ? _.range(size - 5, size - 1) : _.range(0, 5));
    }

    const Item = ({ num }: { num: number }) => (
        <Pag.Item style={{ width: '40px', textAlign: 'center' }} href={href(num)} active={num === selectedPage} onClick={handleClick(num)}>
            {num + 1}
        </Pag.Item>
    );

    if (size <= 1) {
        return null;
    }

    return (
        <Pag size='sm' className='mb-0'>
            <Pag.Prev
                disabled={selectedPage === 0}
                href={href(selectedPage - 1)}
                onClick={handleClick(selectedPage - 1)}
                style={{ width: '40px' }}
            />
            {size <= 7 ?
                _.map(pages, (num) => (
                    <Item key={num} num={num} />
                )) : (
                    <>
                        {(selectedPage >= 4) && <Item num={0} />}
                        {(selectedPage >= 4) && <JumpEllipsis onJump={clampPage} />}
                        {_.map(pages, (num) => (
                            <Item key={num} num={num} />
                        ))}
                        {selectedPage <= size - 5 && <JumpEllipsis onJump={clampPage} />}
                        <Item num={size - 1} />
                    </>
                )}
            <Pag.Next
                href={href(selectedPage + 1)}
                disabled={selectedPage === size - 1}
                onClick={handleClick(selectedPage + 1)}
                style={{ width: '40px' }}
            />
        </Pag>
    );
};

export default Pagination;
